import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useSites from '../../../datastore/useSites';
import useSelectedSite, { SelectedSiteType } from '../../../hooks/useSelectedSite';
import { Site } from '../../../common/types';
import ForcedStoreSelection from '../home/components/ForcedStoreSelection';

// Idle threshold after which a customer must re-confirm their store, so a
// stale logged-in tab doesn't silently place an order at the wrong site.
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
// Shared across tabs via localStorage so activity in one tab keeps another idle tab from prompting.
const LAST_ACTIVITY_KEY = 'TPGE_LAST_ACTIVITY_AT';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

function recordActivity() {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

function getLastActivity(): number {
  const parsed = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Date.now();
}

export default function StoreReselectCoordinator() {
  const location = useLocation();
  const isCheckout = location.pathname.startsWith('/checkout');

  const [selectedSiteId, setSelectedSiteId] = useSelectedSite(
    (state: SelectedSiteType) => [state.selectedSiteId, state.setSelectedSiteId],
  );
  const { sites } = useSites();
  const [needsReselect, setNeedsReselect] = useState(false);
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      recordActivity();
    }
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        recordActivity();
      }
    };
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isCheckout) {
        // Don't let a slow card-entry flow get interrupted by a re-selection prompt.
        recordActivity();
        return;
      }
      if (!selectedSiteId) {
        return;
      }
      if (Date.now() - getLastActivity() >= IDLE_TIMEOUT_MS) {
        setNeedsReselect(true);
      }
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isCheckout, selectedSiteId]);

  const onSiteSelected = (site: Site | null) => {
    if (site) {
      setSelectedSiteId(site.id);
    }
    recordActivity();
    setNeedsReselect(false);
  };

  if (!needsReselect) {
    return null;
  }

  return (
    <ForcedStoreSelection
      sites={sites || []}
      onSiteSelected={onSiteSelected}
      title="Still there?"
      subtitle="Please confirm which store you're ordering from before continuing."
    />
  );
}
